import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { DataTable } from "components/dataTable/index.jsx";
import { useEffect, useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function AdminProducts() {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);

  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState('');

  const [showSkuModal, setShowSkuModal] = useState(false);
  const [skuForm, setSkuForm] = useState({ providerId: '', newProviderId: '', userId: '' });
  const [savingSku, setSavingSku] = useState(false);
  const [loadingSkuPreview, setLoadingSkuPreview] = useState(false);
  const [skuPreview, setSkuPreview] = useState(null);

  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceForm, setPriceForm] = useState({ providerId: '', retailPrice: '', userId: '' });
  const [savingPrice, setSavingPrice] = useState(false);
  const [loadingPricePreview, setLoadingPricePreview] = useState(false);
  const [pricePreview, setPricePreview] = useState(null);

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  useEffect(() => {
    if (!hasMasterScope) return;
    appDataStore.get('/users/list')
      .then(res => setUsers(res.users || []))
      .catch(err => console.error('Error al cargar usuarios:', err));
  }, [hasMasterScope]);

  const columns = useMemo(() => ([
    { accessorKey: 'id', header: 'ID', enableColumnFilter: true },
    {
      id: 'user',
      header: 'Usuario',
      enableColumnFilter: false,
      cell: ({ row }) => {
        const u = row.original.User;
        return u ? <span>{u.name}<br /><span className="text-xs text-gray-500">{u.email}</span></span> : `#${row.original.userId}`;
      }
    },
    { accessorKey: 'category', header: 'Categoría', enableColumnFilter: false },
    { accessorKey: 'name', header: 'Nombre', enableColumnFilter: false },
    { accessorKey: 'providerId', header: 'SKU', enableColumnFilter: true },
    { accessorKey: 'validity', header: 'Meses', enableColumnFilter: false },
    {
      id: 'basePercent',
      header: '% Base',
      enableColumnFilter: false,
      cell: ({ row }) => row.original.User?.basePercent ?? '—'
    },
    {
      accessorKey: 'price',
      header: 'Precio costo',
      enableColumnFilter: false,
      cell: ({ cell }) => formatters.cash(cell.getValue())
    },
    {
      accessorKey: 'retailPrice',
      header: 'Precio venta',
      enableColumnFilter: false,
      cell: ({ cell }) => formatters.cash(cell.getValue())
    },
    {
      accessorKey: 'enabled',
      header: 'Estado',
      enableColumnFilter: false,
      cell: ({ cell }) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${cell.getValue() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {cell.getValue() ? 'Habilitado' : 'Deshabilitado'}
        </span>
      )
    },
    { accessorKey: 'createdAt', header: 'Creado', enableColumnFilter: false, cell: ({ cell }) => moment(cell.getValue()).format('DD/MM/YYYY') },
  ]), []);

  const dataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    const extraFilters = [];
    if (filterUserId) extraFilters.push({ key: 'userId', value: parseInt(filterUserId), operator: 'eq' });

    try {
      const response = await appDataStore.post('/products/admin-list', {
        records,
        currentPage,
        order: order && order.length ? order : [['id', 'DESC']],
        search,
        filter: [...filter, ...extraFilters],
        attributes: ['id', 'userId', 'category', 'name', 'providerId', 'price', 'retailPrice', 'enabled', 'validity', 'createdAt'],
      });

      return {
        data: response.data || [],
        totalRows: response.total || 0,
        totalPages: response.totalPages || 1,
      };
    } catch (err) {
      console.error('Error al cargar productos:', err);
      return { data: [], totalRows: 0, totalPages: 1 };
    }
  }, [appDataStore, filterUserId]);

  const openSkuModal = () => {
    setSkuForm({ providerId: '', newProviderId: '', userId: filterUserId });
    setSkuPreview(null);
    setShowSkuModal(true);
  };

  const closeSkuModal = () => {
    setShowSkuModal(false);
    setSkuPreview(null);
  };

  const handleSkuFormChange = (field, value) => {
    setSkuForm(prev => ({ ...prev, [field]: value }));
    setSkuPreview(null);
  };

  const handlePreviewSku = async () => {
    const { providerId, newProviderId, userId } = skuForm;
    if (!providerId) return alert('Ingresa el SKU actual');
    if (!newProviderId) return alert('Ingresa el nuevo SKU');
    try {
      setLoadingSkuPreview(true);
      const res = await appDataStore.post('/products/admin-bulk-sku-preview', {
        providerId,
        newProviderId,
        userId: userId || undefined,
      });
      setSkuPreview(res.rows || []);
    } catch (err) {
      alert('Error al generar la vista previa: ' + err.message);
    } finally {
      setLoadingSkuPreview(false);
    }
  };

  const handleConfirmSku = async () => {
    const { providerId, newProviderId, userId } = skuForm;
    try {
      setSavingSku(true);
      const res = await appDataStore.post('/products/admin-bulk-sku', {
        providerId,
        newProviderId,
        userId: userId || undefined,
      });
      alert(res.message || 'SKU actualizado');
      closeSkuModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert('Error al cambiar el SKU: ' + err.message);
    } finally {
      setSavingSku(false);
    }
  };

  const openPriceModal = () => {
    setPriceForm({ providerId: '', retailPrice: '', userId: filterUserId });
    setPricePreview(null);
    setShowPriceModal(true);
  };

  const closePriceModal = () => {
    setShowPriceModal(false);
    setPricePreview(null);
  };

  const handlePriceFormChange = (field, value) => {
    setPriceForm(prev => ({ ...prev, [field]: value }));
    setPricePreview(null);
  };

  const handlePreviewPrice = async () => {
    const { providerId, retailPrice, userId } = priceForm;
    if (!providerId) return alert('Ingresa el SKU');
    if (!retailPrice || parseFloat(retailPrice) < 0) return alert('Ingresa un precio de venta válido');
    try {
      setLoadingPricePreview(true);
      const res = await appDataStore.post('/products/admin-bulk-price-preview', {
        providerId,
        retailPrice: parseFloat(retailPrice),
        userId: userId || undefined,
      });
      setPricePreview(res.rows || []);
    } catch (err) {
      alert('Error al generar la vista previa: ' + err.message);
    } finally {
      setLoadingPricePreview(false);
    }
  };

  const handleConfirmPrice = async () => {
    const { providerId, retailPrice, userId } = priceForm;
    try {
      setSavingPrice(true);
      const res = await appDataStore.post('/products/admin-bulk-price', {
        providerId,
        retailPrice: parseFloat(retailPrice),
        userId: userId || undefined,
      });
      alert(res.message || 'Precios actualizados');
      closePriceModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert('Error al cambiar los precios: ' + err.message);
    } finally {
      setSavingPrice(false);
    }
  };

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Productos</h2>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
              <p className="text-red-700">
                No tienes permisos para acceder a la gestión de productos. Se requiere el scope <strong>Master</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2 mx-auto text-black">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Productos</h2>
          <div className="flex gap-2">
            <button
              onClick={openSkuModal}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium text-sm"
            >
              Cambiar SKU masivo
            </button>
            <button
              onClick={openPriceModal}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium text-sm"
            >
              Cambiar precios masivo
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <label className="text-sm font-medium text-gray-700">Filtrar por usuario:</label>
          <select
            value={filterUserId}
            onChange={e => setFilterUserId(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm min-w-[280px]"
          >
            <option value="">Todos los usuarios</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <span className="text-xs text-gray-500">Usa los filtros de columna &quot;ID&quot; o &quot;SKU&quot; en la tabla para buscar por productId o SKU.</span>
        </div>

        <DataTable
          key={`${refreshKey}-${filterUserId}`}
          columns={columns}
          dataFetch={dataFetch}
          refreshDependencies={filterUserId}
          exportFileName={`productos_${moment().format('DDMMYY')}`}
        />
      </div>

      {/* Modal: Cambiar SKU masivo */}
      {showSkuModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg p-6 w-full shadow-xl ${skuPreview ? 'max-w-3xl' : 'max-w-md'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Cambiar SKU masivamente</h3>
              <button onClick={closeSkuModal} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Reemplaza el SKU (Provider ID) actual por uno nuevo en todos los productos que coincidan.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU actual *</label>
                <input type="text" placeholder="Ej: bundle.standard" value={skuForm.providerId}
                  onChange={e => handleSkuFormChange('providerId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU nuevo *</label>
                <input type="text" placeholder="Ej: bundle.premium" value={skuForm.newProviderId}
                  onChange={e => handleSkuFormChange('newProviderId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restringir a un usuario (opcional)</label>
                <select
                  value={skuForm.userId}
                  onChange={e => handleSkuFormChange('userId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Todos los usuarios</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>

            {skuPreview && (
              <div className="mt-5">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Vista previa: {skuPreview.length} producto(s) serán modificados
                </p>
                {skuPreview.length === 0 ? (
                  <div className="text-sm text-gray-500 py-3">No hay productos que coincidan con ese SKU.</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">ID</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">Usuario</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">Producto</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">SKU actual</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">SKU nuevo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skuPreview.map(row => (
                          <tr key={row.id} className="border-b border-gray-100">
                            <td className="px-2 py-1.5 text-gray-500 font-mono">{row.id}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.userName || `#${row.userId}`}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.name}</td>
                            <td className="px-2 py-1.5 font-mono text-gray-500">{row.providerId}</td>
                            <td className="px-2 py-1.5 font-mono text-indigo-700">{row.newProviderId}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              {!skuPreview ? (
                <button onClick={handlePreviewSku} disabled={loadingSkuPreview}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                  {loadingSkuPreview ? 'Generando vista previa...' : 'Vista previa'}
                </button>
              ) : (
                <>
                  <button onClick={handleConfirmSku} disabled={savingSku || skuPreview.length === 0}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                    {savingSku ? 'Aplicando...' : `Confirmar y aplicar (${skuPreview.length})`}
                  </button>
                  <button onClick={() => setSkuPreview(null)} disabled={savingSku}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                    Volver
                  </button>
                </>
              )}
              <button onClick={closeSkuModal} disabled={savingSku || loadingSkuPreview}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cambiar precios masivo */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg p-6 w-full shadow-xl ${pricePreview ? 'max-w-4xl' : 'max-w-md'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Cambiar precios masivamente</h3>
              <button onClick={closePriceModal} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Define el precio de venta de 1 mes para un SKU. Para cada producto, el precio de venta se
              multiplica por su vigencia (meses) y el precio de costo se calcula automáticamente con el
              % base del usuario dueño: <span className="font-mono">venta = precio 1 mes × vigencia</span>,{' '}
              <span className="font-mono">costo = venta - (venta × %base / 100)</span>.
              Los productos de usuarios sin % base configurado se omiten.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                <input type="text" placeholder="Ej: bundle.standard" value={priceForm.providerId}
                  onChange={e => handlePriceFormChange('providerId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio de venta (1 mes) *</label>
                <input type="number" step="0.01" min="0" placeholder="0.00" value={priceForm.retailPrice}
                  onChange={e => handlePriceFormChange('retailPrice', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restringir a un usuario (opcional)</label>
                <select
                  value={priceForm.userId}
                  onChange={e => handlePriceFormChange('userId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Todos los usuarios</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>

            {pricePreview && (
              <div className="mt-5">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Vista previa: {pricePreview.length} producto(s) coinciden
                  {pricePreview.some(r => r.skipped) && (
                    <span className="text-amber-600"> ({pricePreview.filter(r => r.skipped).length} se omitirán por no tener % base)</span>
                  )}
                </p>
                {pricePreview.length === 0 ? (
                  <div className="text-sm text-gray-500 py-3">No hay productos que coincidan con ese SKU.</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">ID</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">Usuario</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">Producto</th>
                          <th className="px-2 py-1.5 text-center font-semibold text-gray-700 border-b border-gray-300">Meses</th>
                          <th className="px-2 py-1.5 text-center font-semibold text-gray-700 border-b border-gray-300">% Base</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border-b border-gray-300">Venta actual</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border-b border-gray-300">Venta nueva</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border-b border-gray-300">Costo actual</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-gray-700 border-b border-gray-300">Costo nuevo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricePreview.map(row => (
                          <tr key={row.id} className={`border-b border-gray-100 ${row.skipped ? 'bg-amber-50 text-gray-400' : ''}`}>
                            <td className="px-2 py-1.5 text-gray-500 font-mono">{row.id}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.userName || `#${row.userId}`}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.name}</td>
                            <td className="px-2 py-1.5 text-center">{row.validity}</td>
                            <td className="px-2 py-1.5 text-center">{row.basePercent ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{formatters.cash(row.currentRetailPrice)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-blue-700">{row.skipped ? '—' : formatters.cash(row.newRetailPrice)}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{formatters.cash(row.currentPrice)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-blue-700">{row.skipped ? '—' : formatters.cash(row.newPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              {!pricePreview ? (
                <button onClick={handlePreviewPrice} disabled={loadingPricePreview}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                  {loadingPricePreview ? 'Generando vista previa...' : 'Vista previa'}
                </button>
              ) : (
                <>
                  <button onClick={handleConfirmPrice} disabled={savingPrice || pricePreview.filter(r => !r.skipped).length === 0}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                    {savingPrice ? 'Aplicando...' : `Confirmar y aplicar (${pricePreview.filter(r => !r.skipped).length})`}
                  </button>
                  <button onClick={() => setPricePreview(null)} disabled={savingPrice}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                    Volver
                  </button>
                </>
              )}
              <button onClick={closePriceModal} disabled={savingPrice || loadingPricePreview}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});